/**
 * Cálculo de la DJ 1947 (Pro Pyme Transparente, 14 D N°8)
 *
 * Único lugar con las líneas del recuadro 22 del F22 AT 2026, sus totales y el reparto
 * a cada propietario. Lo usan el servidor (al guardar y al generar el CSV), la pantalla
 * (para mostrar el reparto mientras se edita) y, después, la renta 14 D N°8, que lee la
 * base de la DJ en vez de recalcularla.
 *
 * Reparto: según las instrucciones de la DJ 1947, la base imponible, los créditos y los
 * PPM se asignan en la proporción en que cada propietario participa en las utilidades.
 * Cada columna se redondea al peso y la diferencia del redondeo queda en el propietario
 * con mayor porcentaje, para que la suma de los propietarios sea igual al total.
 *
 * Certificados: el número es automático y correlativo, y parte de 1 en cada DJ, según el
 * orden de los propietarios.
 */

// Recuadro 22 del F22 AT 2026, en su orden. «calculada»: no se ingresa, sale de otros datos.
const LINEAS_INGRESOS = [
    { codigo: '1600', titulo: 'Ingresos del giro percibidos' },
    { codigo: '1819', titulo: 'Ingresos del giro devengados en ejercicios anteriores y percibidos en el ejercicio actual' },
    { codigo: '1601', titulo: 'Rentas de fuente extranjera percibidas' },
    { codigo: '1602', titulo: 'Intereses y reajustes percibidos por préstamos y otros' },
    { codigo: '1603', titulo: 'Mayor valor percibido por rescate o enajenación de inversiones o bienes no depreciables' },
    { codigo: '1604', titulo: 'Dividendos o retiros percibidos en el ejercicio, por participaciones en otras empresas' },
    { codigo: '1605', titulo: 'Incremento por IDPC', calculada: true },
    { codigo: '1606', titulo: 'Ingresos percibidos o devengados por operaciones con empresas relacionadas del art. 14 letra A) LIR' },
    { codigo: '1607', titulo: 'Otros ingresos percibidos o devengados' },
    { codigo: '1608', titulo: 'Ingreso diferido imputado en el ejercicio, debidamente incrementado y reajustado, cuando corresponda' },
    { codigo: '1609', titulo: 'Crédito por activos fijos adquiridos en el ejercicio (art. 33 bis LIR)' },
];

const LINEAS_EGRESOS = [
    { codigo: '1611', titulo: 'Gasto por saldo inicial de existencias o insumos del negocio en cambio de régimen, pagados' },
    { codigo: '1612', titulo: 'Gasto por saldo inicial de activos fijos depreciables en cambio de régimen, pagados' },
    { codigo: '1613', titulo: 'Gasto por pérdida tributaria en cambio de régimen' },
    { codigo: '1614', titulo: 'Existencias, insumos y servicios del negocio, pagados' },
    { codigo: '1820', titulo: 'Existencias, insumos y servicios del negocio adeudados en ejercicios anteriores y pagados en el ejercicio actual' },
    { codigo: '1615', titulo: 'Gastos de rentas de fuente extranjera, pagados' },
    { codigo: '1616', titulo: 'Remuneraciones pagadas' },
    { codigo: '1617', titulo: 'Honorarios pagados' },
    { codigo: '1618', titulo: 'Adquisición de bienes del activo fijo, pagados' },
    { codigo: '1620', titulo: 'Arriendos pagados' },
    { codigo: '1621', titulo: 'Gastos por exigencias medio ambientales, pagados' },
    { codigo: '1622', titulo: 'Intereses y reajustes pagados por préstamos y otros' },
    { codigo: '1624', titulo: 'Pérdida en rescate o enajenación de inversiones o bienes no depreciables' },
    { codigo: '1625', titulo: 'Otros gastos deducibles de los ingresos' },
    { codigo: '1626', titulo: 'Gastos o egresos pagados o adeudados por operaciones con empresas relacionadas del art. 14 letra A) LIR' },
    { codigo: '1627', titulo: 'Pérdidas tributarias de ejercicios anteriores' },
    { codigo: '1628', titulo: 'Créditos incobrables castigados en el ejercicio (reconocidos sobre ingresos devengados)' },
    { codigo: '1909', titulo: 'Gastos aceptados por donaciones' },
];

// Créditos por IDPC de las participaciones en otras empresas (columnas C6 a C9); su suma
// es el 1605
const CREDITOS_IDPC = [
    { campo: 'credito_idpc_no_sujeto_sin_dev', columna: 'C6', titulo: 'No sujeto a restitución, sin derecho a devolución' },
    { campo: 'credito_idpc_no_sujeto_con_dev', columna: 'C7', titulo: 'No sujeto a restitución, con derecho a devolución' },
    { campo: 'credito_idpc_sujeto_sin_dev', columna: 'C8', titulo: 'Sujeto a restitución, sin derecho a devolución' },
    { campo: 'credito_idpc_sujeto_con_dev', columna: 'C9', titulo: 'Sujeto a restitución, con derecho a devolución' },
];

// El crédito por ingreso diferido sujeto a restitución se informa castigado al 65 %
const CASTIGO_SUJETO_RESTITUCION = 0.65;

// Campos de dj1947_base que se ingresan (además de las líneas cod_XXXX)
const CAMPOS_BASE = [
    'ppm_historico', 'ppm_reajuste',
    ...CREDITOS_IDPC.map((credito) => credito.campo),
    'credito_ing_dif_no_sujeto', 'credito_ing_dif_sujeto',
];

const CAMPOS_LINEAS = [...LINEAS_INGRESOS, ...LINEAS_EGRESOS]
    .filter((linea) => !linea.calculada)
    .map((linea) => `cod_${linea.codigo}`);

// Columnas de la sección B que llevan montos, en el orden del formulario F1947
const COLUMNAS_MONTO = [
    { campo: 'base_imponible', columna: 'C3', titulo: 'Base imponible a tributar con impuestos finales' },
    { campo: 'ingreso_diferido', columna: 'C4', titulo: 'Ingreso diferido contenido en la base imponible' },
    { campo: 'retiros', columna: 'C5', titulo: 'Retiros, remesas o distribuciones del ejercicio' },
    ...CREDITOS_IDPC.map(({ campo, columna, titulo }) => ({ campo, columna, titulo: `Crédito por IDPC: ${titulo.toLowerCase()}` })),
    { campo: 'credito_ing_dif_no_sujeto', columna: 'C11', titulo: 'Crédito por ingreso diferido no sujeto a restitución' },
    { campo: 'credito_ing_dif_sujeto', columna: 'C12', titulo: 'Crédito por ingreso diferido sujeto a restitución (castigado, 65 %)' },
    { campo: 'credito_33_bis', columna: 'C13', titulo: 'Crédito artículo 33 bis de la LIR' },
    { campo: 'ppm', columna: 'C14', titulo: 'PPM puesto a disposición de los propietarios' },
];

const numero = (valor) => {
    const n = Math.round(Number(valor));
    return Number.isFinite(n) ? n : 0;
};

const suma = (valores) => valores.reduce((total, valor) => total + valor, 0);

const incrementoIDPC = (base) => suma(CREDITOS_IDPC.map((credito) => numero(base[credito.campo])));

/**
 * Monto de una línea del recuadro 22
 * @param {Object} base - Fila de dj1947_base (o el formulario en pantalla)
 * @param {string} codigo
 */
const valorLinea = (base, codigo) => (codigo === '1605' ? incrementoIDPC(base) : numero(base[`cod_${codigo}`]));

/**
 * Totales del recuadro 22 y de lo que se reparte
 * @param {Object} base
 * @returns {{ ingresos: number, egresos: number, baseImponible: number, incrementoIDPC: number, ppmActualizado: number }}
 */
function totales(base) {
    const ingresos = suma(LINEAS_INGRESOS.map((linea) => valorLinea(base, linea.codigo)));      // 1610
    const egresos = suma(LINEAS_EGRESOS.map((linea) => valorLinea(base, linea.codigo)));        // 1629
    return {
        ingresos,
        egresos,
        baseImponible: ingresos - egresos,                                                       // 1630
        incrementoIDPC: incrementoIDPC(base),
        ppmActualizado: numero(base.ppm_historico) + numero(base.ppm_reajuste),
    };
}

/**
 * Reparte un total en pesos según el porcentaje de cada propietario. La diferencia del
 * redondeo queda en el de mayor porcentaje (el primero, si hay empate).
 * @param {number} total
 * @param {Array<{ porcentaje: number|string }>} propietarios
 * @returns {number[]}
 */
function repartir(total, propietarios) {
    if (!propietarios.length || total === 0) return propietarios.map(() => 0);
    const montos = propietarios.map((p) => Math.round(total * Number(p.porcentaje || 0) / 100));
    let mayor = 0;
    propietarios.forEach((p, i) => { if (Number(p.porcentaje || 0) > Number(propietarios[mayor].porcentaje || 0)) mayor = i; });
    montos[mayor] += total - suma(montos);
    return montos;
}

/**
 * Montos de cada propietario (sección B de la DJ 1947)
 * @param {Object} base - Fila de dj1947_base
 * @param {Array<Object>} propietarios - Filas de dj1947_propietarios, en su orden
 * @returns {Array<Object>} Una fila por propietario con rut, nombre, porcentaje,
 *   numero_certificado (1, 2, 3… en el orden) y los campos de COLUMNAS_MONTO
 */
function reparto(base, propietarios) {
    const t = totales(base);
    const totalesPorColumna = {
        // Una pérdida tributaria no se asigna a los propietarios: queda en la empresa
        base_imponible: Math.max(t.baseImponible, 0),
        ingreso_diferido: numero(base.cod_1608),
        ...Object.fromEntries(CREDITOS_IDPC.map((credito) => [credito.campo, numero(base[credito.campo])])),
        credito_ing_dif_no_sujeto: numero(base.credito_ing_dif_no_sujeto),
        credito_ing_dif_sujeto: Math.round(numero(base.credito_ing_dif_sujeto) * CASTIGO_SUJETO_RESTITUCION),
        credito_33_bis: numero(base.cod_1609),
        ppm: t.ppmActualizado,
    };
    const repartidos = Object.fromEntries(
        Object.entries(totalesPorColumna).map(([campo, total]) => [campo, repartir(total, propietarios)])
    );
    return propietarios.map((p, i) => ({
        rut: p.rut,
        nombre: p.nombre,
        tipo_persona: p.tipo_persona,
        porcentaje: Number(p.porcentaje || 0),
        numero_certificado: i + 1,
        ...Object.fromEntries(Object.keys(totalesPorColumna).map((campo) => [campo, repartidos[campo][i]])),
        retiros: numero(p.retiros),
    }));
}

/**
 * Totales de la sección B (cuadro resumen de la declaración)
 * @param {Array<Object>} filas - Resultado de reparto()
 */
function totalesDeclaracion(filas) {
    return {
        casos: filas.length,
        ...Object.fromEntries(COLUMNAS_MONTO.map(({ campo }) => [campo, suma(filas.map((fila) => fila[campo]))])),
    };
}

/**
 * Errores (impiden guardar y generar el CSV) y avisos (no bloquean)
 * @param {Object} base
 * @param {Array<Object>} propietarios
 * @returns {{ errores: string[], avisos: string[] }}
 */
function revisar(base, propietarios) {
    const errores = [];
    const avisos = [];
    if (!propietarios.length) {
        errores.push('No hay propietarios. Regístralos en Socios y dividendos y vuelve a traer los datos de TAXI.');
    } else {
        const sumaPorcentajes = suma(propietarios.map((p) => Number(p.porcentaje || 0)));
        if (Math.abs(sumaPorcentajes - 100) > 0.0001) {
            errores.push(`Los porcentajes de los propietarios suman ${sumaPorcentajes.toLocaleString('es-CL')} % y deben sumar 100 %.`);
        }
    }
    for (const p of propietarios) {
        const porcentaje = Number(p.porcentaje);
        if (!Number.isFinite(porcentaje) || porcentaje < 0 || porcentaje > 100) {
            errores.push(`El porcentaje de ${p.nombre || p.rut} debe estar entre 0 y 100.`);
        }
        if (p.tipo_persona === 'juridica') {
            avisos.push(`${p.nombre || p.rut} está registrado como persona jurídica: en el régimen 14 D N°8 todos los propietarios deben pagar impuestos finales.`);
        }
    }
    const t = totales(base);
    if (t.baseImponible < 0) {
        avisos.push('La base imponible es una pérdida tributaria: no se asigna a los propietarios.');
    }
    const sinNada = t.baseImponible <= 0 && t.ppmActualizado === 0 && t.incrementoIDPC === 0
        && !propietarios.some((p) => numero(p.retiros) > 0);
    if (sinNada) {
        avisos.push('Sin base imponible, créditos, PPM ni retiros que informar, la DJ 1947 no se presenta (instrucciones del SII).');
    }
    return { errores, avisos };
}

module.exports = {
    LINEAS_INGRESOS,
    LINEAS_EGRESOS,
    CREDITOS_IDPC,
    CAMPOS_BASE,
    CAMPOS_LINEAS,
    COLUMNAS_MONTO,
    CASTIGO_SUJETO_RESTITUCION,
    valorLinea,
    totales,
    repartir,
    reparto,
    totalesDeclaracion,
    revisar,
};
